import { ShiftTemplate } from '../types';

const PALETTE_SIZE = 1024;

function hslToHex(h: number, s: number, l: number): string {
  const sat = s / 100;
  const light = l / 100;
  const c = (1 - Math.abs(2 * light - 1)) * sat;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = light - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }

  const toHex = (value: number) =>
    Math.round((value + m) * 255)
      .toString(16)
      .padStart(2, '0');

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Matches AssignShiftTemplateDistinctColors.sql palette generation. */
export function colorFromPaletteIndex(index: number): string {
  const idx = ((index % PALETTE_SIZE) + PALETTE_SIZE) % PALETTE_SIZE;
  const hue = (idx * 137.508) % 360;
  const sat = 52 + (idx % 4) * 5;
  const lit = 46 + (idx % 5) * 4;
  return hslToHex(hue, sat, lit);
}

function parseTimeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map((part) => Number(part));
  return (hours || 0) * 60 + (minutes || 0);
}

export function compareShiftTemplateTimes(a: string, b: string): number {
  return parseTimeToMinutes(a) - parseTimeToMinutes(b);
}

export function sortShiftTemplatesByStartTime<T extends { startTime: string; endTime?: string; id?: number }>(
  templates: T[]
): T[] {
  return [...templates].sort((a, b) => {
    const byStart = compareShiftTemplateTimes(a.startTime, b.startTime);
    if (byStart !== 0) return byStart;
    if (a.endTime && b.endTime) {
      const byEnd = compareShiftTemplateTimes(a.endTime, b.endTime);
      if (byEnd !== 0) return byEnd;
    }
    return (a.id ?? 0) - (b.id ?? 0);
  });
}

export function buildShiftTemplateColorMap(
  templates: Array<Pick<ShiftTemplate, 'id' | 'startTime' | 'endTime'>>
): Map<number, string> {
  const map = new Map<number, string>();
  sortShiftTemplatesByStartTime(templates).forEach((template, index) => {
    map.set(template.id, colorFromPaletteIndex(index));
  });
  return map;
}

export function resolveShiftTemplateColor(
  template: Pick<ShiftTemplate, 'id' | 'startTime' | 'endTime' | 'color'>,
  colorMap?: Map<number, string>
): string {
  if (colorMap?.has(template.id)) {
    return colorMap.get(template.id)!;
  }

  return colorFromPaletteIndex(Math.max(0, template.id - 1));
}

export function shiftTemplateLightBackground(color: string): string {
  return `${color}20`;
}
